import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ModernIcons } from '../components/ModernIcons';
import './CommunityPage.css';

interface Message {
  id: number;
  author: string;
  avatar: string;
  content: string;
  timestamp: string;
  likes: number;
  isLiked: boolean;
}

interface ForumPost {
  id: number;
  title: string;
  author: string;
  avatar: string;
  content: string;
  timestamp: string;
  category: string;
  replies: number;
  views: number;
}

const CommunityPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'chat' | 'forum'>('chat');
  const [newMessage, setNewMessage] = useState('');
  const [newPost, setNewPost] = useState({ title: '', content: '' });

  const messages: Message[] = [
    {
      id: 1,
      author: 'Анна',
      avatar: '👩‍💼',
      content: 'Привет! Кто-нибудь уже попробовал новую функцию планирования?',
      timestamp: '10:30',
      likes: 5,
      isLiked: false
    },
    {
      id: 2,
      author: 'Мария',
      avatar: '👩‍🎨',
      content: 'Да, очень удобно! Особенно нравится трекер привычек',
      timestamp: '10:32',
      likes: 3,
      isLiked: true
    },
    {
      id: 3,
      author: 'Елена',
      avatar: '👩‍🔬',
      content: 'А как вы организуете свой день? Поделитесь лайфхаками!',
      timestamp: '10:35',
      likes: 7,
      isLiked: false
    }
  ];

  const forumPosts: ForumPost[] = [
    {
      id: 1,
      title: 'Как найти баланс между работой и личной жизнью?',
      author: 'София',
      avatar: '👩‍⚕️',
      content: 'Делимся опытом и советами по планированию времени...',
      timestamp: '2 часа назад',
      category: 'Планирование',
      replies: 12,
      views: 45
    },
    {
      id: 2,
      title: 'Лучшие приложения для отслеживания здоровья',
      author: 'Катя',
      avatar: '👩‍🏫',
      content: 'Обзор приложений и их функций для мониторинга здоровья...',
      timestamp: '5 часов назад',
      category: 'Здоровье',
      replies: 8,
      views: 32
    },
    {
      id: 3,
      title: 'Мотивация для саморазвития: личный опыт',
      author: 'Алиса',
      avatar: '👩‍💻',
      content: 'История моего пути к личностному росту и развитию...',
      timestamp: '1 день назад',
      category: 'Развитие',
      replies: 15,
      views: 67
    }
  ];

  const categories = ['Все', 'Планирование', 'Здоровье', 'Развитие', 'Благополучие'];
  const [selectedCategory, setSelectedCategory] = useState('Все');

  const filteredPosts = selectedCategory === 'Все' 
    ? forumPosts 
    : forumPosts.filter(post => post.category === selectedCategory);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      // Здесь будет логика отправки сообщения
      setNewMessage('');
    }
  };

  const handleCreatePost = () => {
    if (newPost.title.trim() && newPost.content.trim()) {
      // Здесь будет логика создания поста
      setNewPost({ title: '', content: '' });
    }
  };

  return (
    <div className="community-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Рядом Club</h1>
              <p className="text-gray-600 mt-1">Сообщество пользователей приложения</p>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <ModernIcons.User size={16} />
              <span>1,247 участников</span>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'chat'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ModernIcons.Heart size={16} className="inline mr-2" />
              Чат
            </button>
            <button
              onClick={() => setActiveTab('forum')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'forum'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ModernIcons.BookOpen size={16} className="inline mr-2" />
              Форум
            </button>
          </div>
        </motion.div>

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {/* Messages */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-gray-200 h-96 overflow-hidden flex flex-col" style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}}>
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Общий чат</h3>
                  <p className="text-sm text-gray-600">Общайтесь с другими участниками</p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((message) => (
                    <div key={message.id} className="flex space-x-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm">
                        {message.avatar}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-sm text-gray-900">{message.author}</span>
                          <span className="text-xs text-gray-500">{message.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{message.content}</p>
                        <button className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700">
                          <ModernIcons.Heart size={12} className={message.isLiked ? 'text-red-500' : ''} />
                          <span>{message.likes}</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-gray-200">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Напишите сообщение..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <button
                      onClick={handleSendMessage}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                    >
                      Отправить
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Online Users */}
            <div>
              <div className="bg-white rounded-xl border border-gray-200 p-6" style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}}>
                <h3 className="font-semibold text-gray-900 mb-4">Онлайн сейчас</h3>
                <div className="space-y-3">
                  {['Анна', 'Мария', 'Елена', 'София'].map((user, index) => (
                    <div key={user} className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm">
                        {['👩‍💼', '👩‍🎨', '👩‍🔬', '👩‍⚕️'][index]}
                      </div>
                      <span className="text-sm text-gray-700">{user}</span>
                      <div className="w-2 h-2 bg-green-500 rounded-full ml-auto"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Forum Tab */}
        {activeTab === 'forum' && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Categories */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Create Post */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}}>
              <h3 className="font-semibold text-gray-900 mb-4">Создать новый пост</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="Заголовок поста"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  placeholder="Содержание поста"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
                <button
                  onClick={handleCreatePost}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  <ModernIcons.Plus size={16} className="inline mr-2" />
                  Опубликовать
                </button>
              </div>
            </div>

            {/* Forum Posts */}
            <div className="space-y-4">
              {filteredPosts.map((post) => (
                <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-6 transition-shadow" style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}} onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-lg">
                      {post.avatar}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-semibold text-gray-900">{post.author}</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-500">{post.timestamp}</span>
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">{post.title}</h3>
                      <p className="text-gray-700 text-sm mb-3">{post.content}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <button className="flex items-center space-x-1 hover:text-gray-700">
                          <ModernIcons.Heart size={14} />
                          <span>Нравится</span>
                        </button>
                        <span className="flex items-center space-x-1">
                          <ModernIcons.Edit size={14} />
                          <span>{post.replies} ответов</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <ModernIcons.Search size={14} />
                          <span>{post.views} просмотров</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CommunityPage;
